import { useQuery } from "@tanstack/react-query";
import { parseISO, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

export interface AdminApplicationRow {
  id: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  bakery_name: string;
  abn: string;
  website: string | null;
  instagram: string | null;
  years_in_business: string;
  product_types: string[];
  dietary_options: string[];
  customisation_level: string;
  signature_style: string;
  price_range: string;
  delivery_type: string;
  delivery_suburbs: string;
  min_notice_days: number;
  max_orders_per_week: string;
  team_sizes_served: string[];
  handles_urgent_orders: string;
  existing_corporate_clients: string | null;
  why_lembra: string;
  additional_notes: string | null;
  referral_source: string | null;
  status: string;
  admin_notes: string | null;
  reviewed_at: string | null;
  created_at: string | null;
}

export interface AdminNoticeRow {
  id: string;
  title: string;
  message: string;
  priority: string;
  published_at: string | null;
  expires_at: string | null;
  created_by: string | null;
  created_at: string | null;
}

export interface AdminPartnerRow {
  id: string;
  profile_id: string | null;
  application_id: string | null;
  vendor_name: string;
  contact_email: string;
  abn: string;
  website: string | null;
  instagram: string | null;
  product_types: string[] | null;
  dietary_options: string[] | null;
  delivery_suburbs: string | null;
  is_active: boolean | null;
  created_at: string | null;
}

export interface AdminCompanyRow {
  id: string;
  name: string;
  team_size: string | null;
  industry: string | null;
  celebration_type: string | null;
  celebration_style: string | null;
  monthly_budget: string | null;
  created_at: string | null;
  order_count: number;
  last_order_date: string | null;
  member_count: number;
  activation_status: "pending" | "active" | null;
  created_by_admin: boolean | null;
  admin_invitee_email: string | null;
  activated_at: string | null;
}

export interface AdminOrderRow {
  id: string;
  reference: string;
  team_member_name: string;
  celebration_type: string;
  cake_type: string | null;
  cake_size: string | null;
  dietary_needs: string[] | null;
  special_instructions: string | null;
  delivery_address: string | null;
  delivery_date: string | null;
  delivery_time: string | null;
  budget_cents: number;
  status: string;
  notes: string | null;
  created_at: string | null;
  company_id: string;
  company_name: string;
}

// Sprint 1.7j, gift-request rows (orders originated via the new
// chat / browse / OrderConfirmSheet flow). Distinguished from the
// legacy cake flow by the cake_size column carrying the
// "<vendor_category> · <vendor_name>" composite that submit-gift-
// request writes. Surfaces the parsed bits so the operator console
// can render proper category labels without reaching into broadcast.ts.
export interface AdminGiftRequestRow {
  id: string;
  reference: string;
  team_member_name: string;
  celebration_type: string;
  // The SKU name (cake_type column, legacy name)
  product_name: string | null;
  vendor_category: string | null;
  vendor_name: string | null;
  customer_email: string | null;
  customer_name: string | null;
  delivery_address: string | null;
  delivery_date: string | null;
  budget_cents: number;
  status: string;
  payment_status: string | null;
  company_id: string | null;
  company_name: string | null;
  // Most-recent celebration_recommendations row linked to this order
  // (gives ops the customer's request reference + when they submitted).
  recommendation_request_ref: string | null;
  recommendation_status: string | null;
  // Sprint 1.7p, pointer to the chat session that produced the
  // order (when the order came from chat). Null for browse orders.
  chat_session_id: string | null;
  created_at: string | null;
  // Sprint 1.7s, used to compute time-to-fulfilment for the ops pulse
  // (delivered orders use updated_at as a proxy for the fulfilled-at
  // timestamp; we don't have a dedicated `delivered_at` column).
  updated_at: string | null;
}

export interface AdminRecentOrder {
  budget_cents: number;
  delivery_date: string | null;
  status: string;
  reference: string;
  team_member_name: string;
  celebration_type: string;
  created_at: string | null;
  // MS228: vendor_category drives the category-aware status badge on the
  // admin recent-orders list, matching MS214's customer-side fix.
  vendor_category: string | null;
}

export interface AdminOverview {
  totalCompanies: number;
  totalOrders: number;
  ordersThisMonth: number;
  revenueThisMonth: number;
  activePartners: number;
  /** MS307: vendors that are still vendor_partners.is_active=true but
   *  have zero is_available=true SKUs (full catalogue pause, like
   *  Chéri since MS283). Derived from the same data the rest of the
   *  catalogue-honesty surfaces use (MS283/285/293/296/297/304/306).
   *  Surfaced as a top-level stat so Lucky's daily scan flags
   *  reactivation candidates without needing to drill into the vendor
   *  table. Counted against activePartners — pausedPartners ≤
   *  activePartners is always true. */
  pausedPartners: number;
  pendingApplications: number;
  recentOrders: AdminRecentOrder[];
}

/**
 * All bakery applications for /admin/applications. Mutations (approve,
 * reject, waitlist) should invalidate ["admin", "applications"] +
 * ["admin", "platform-overview"] so the overview's pending-count stat
 * reconciles.
 */
export function useAdminApplications() {
  return useQuery({
    queryKey: ["admin", "applications"],
    staleTime: 30_000,
    queryFn: async (): Promise<AdminApplicationRow[]> => {
      const { data, error } = await supabase
        .from("bakery_applications")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as unknown as AdminApplicationRow[] | null) ?? [];
    },
  });
}

/**
 * All admin-issued bakery notices for /admin/notices, including expired
 * entries (the page splits them client-side). Create/delete mutations
 * should invalidate ["admin", "notices"] + ["bakery_notices", "active"]
 * so the bakery-dashboard banner reconciles too.
 */
export function useAdminNotices() {
  return useQuery({
    queryKey: ["admin", "notices"],
    staleTime: 30_000,
    queryFn: async (): Promise<AdminNoticeRow[]> => {
      const { data, error } = await supabase
        .from("bakery_notices")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as unknown as AdminNoticeRow[] | null) ?? [];
    },
  });
}

// ── Sprint 2.1b, Vendor performance rollup ────────────────────────
//
// Per-vendor aggregation across chat/browse-originated orders. Joins
// vendor_partners (canonical vendor list) with orders (filtered to the
// Sprint 1.7+ shape via cake_size LIKE '%·%') and rolls up:
//
//   - Order counts (paid vs pending vs cancelled)
//   - Revenue (paid total + pending total)
//   - Avg fulfilment time (created_at → updated_at on delivered rows)
//   - Last-order date
//
// Vendors with zero orders still surface, useful to see who's in the
// catalogue but not selling. Inactive vendors are excluded from the
// default view to keep the operator's table clean.
//
// Legacy cake orders (cake_type = bakery name, no '·' separator) are
// deliberately scope-cut for v1. Lucky's pivoting away from that flow
// and double-counting both shapes adds attribution complexity that
// won't pay off.

export interface AdminVendorRow {
  id: string;             // vendor_partners.id when known, else slugified name
  name: string;           // vendor_partners.vendor_name (canonical) or parsed from cake_size
  slug: string;           // URL-safe; for the future drill-down route
  is_active: boolean;     // false = inactive partner, included only when filter requested
  is_known_partner: boolean; // false = name appeared in cake_size but no vendor_partners row
  contact_email: string | null;
  paid_orders: number;
  pending_orders: number;
  cancelled_orders: number;
  // MS551-followup-A: dollars (orders.budget_cents stored as integer cents,
  // divided to dollars at this API boundary so admin UI stays unchanged).
  paid_revenue: number;
  pending_revenue: number;
  avg_fulfil_hours: number | null; // mean of (updated_at - created_at) on delivered rows
  last_order_at: string | null;    // ISO; ordering signal for "alive" vendors
  // MS306: catalogue availability surface. `available_sku_count`
  // counts vendor_products where is_available=true AND deleted_at IS
  // NULL — the same filter the public catalogue surfaces use
  // (MS283/285/293/296/297/304). `is_paused` derives the boolean for
  // convenient UI gating: a known partner with zero available SKUs.
  // Unknown vendors (rows parsed from orders.cake_size with no
  // vendor_partners row) can't be paused since there's no catalogue
  // to check — they always come back as `available_sku_count: 0,
  // is_paused: false` so the admin "unknown partner" pill remains
  // the dominant signal for those rows.
  available_sku_count: number;
  is_paused: boolean;
}

function vendorSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function useAdminVendors() {
  return useQuery({
    queryKey: ["admin", "vendors"],
    staleTime: 30_000,
    queryFn: async (): Promise<AdminVendorRow[]> => {
      const [partnersRes, ordersRes, productsRes] = await Promise.all([
        supabase
          .from("vendor_partners")
          .select("id, vendor_name, contact_email, is_active, created_at"),
        supabase
          .from("orders")
          .select("cake_size, budget_cents, status, payment_status, created_at, updated_at")
          .like("cake_size", "%·%")
          // MS408: cap to prevent an unbounded full-table scan on every
          // mount of /admin/vendors as the order volume grows. The vendor
          // performance rollup is a display metric — 2000 most-recent
          // orders is sufficient for an operational "who's selling" view.
          .limit(2000),
        // MS306: count available SKUs per vendor_partner_id so we can
        // surface a "Paused" badge for vendors whose entire SKU set
        // is is_available=false (e.g. Chéri since MS283). Single
        // query alongside the existing partner/orders fetch, no extra
        // round-trip cost. Mirrors the filter shape in MS283's RPC +
        // MS296's useVendorIndex.
        supabase
          .from("vendor_products")
          .select("vendor_partner_id")
          .is("deleted_at", null)
          .eq("is_available", true),
      ]);
      if (partnersRes.error) throw partnersRes.error;
      if (ordersRes.error)   throw ordersRes.error;
      if (productsRes.error) throw productsRes.error;

      // Build vendor_partner_id → available SKU count map.
      const skuCountByVendor = new Map<string, number>();
      for (const p of (productsRes.data as { vendor_partner_id: string }[] | null) ?? []) {
        skuCountByVendor.set(
          p.vendor_partner_id,
          (skuCountByVendor.get(p.vendor_partner_id) ?? 0) + 1,
        );
      }

      type Partner = {
        id: string;
        vendor_name: string;
        contact_email: string | null;
        is_active: boolean | null;
        created_at: string | null;
      };
      type Order = {
        cake_size: string | null;
        budget_cents: number | null;
        status: string;
        payment_status: string | null;
        created_at: string | null;
        updated_at: string | null;
      };
      const partners = (partnersRes.data as Partner[] | null) ?? [];
      const orders = (ordersRes.data as Order[] | null) ?? [];

      // Build a vendor-key index. Keyed by lowercased trimmed vendor_name
      // so the lookup tolerates casing drift between cake_size and the
      // canonical vendor_partners.vendor_name.
      const keyOf = (n: string) => n.trim().toLowerCase();

      type Bucket = {
        partner: Partner | null;
        displayName: string;
        paid_orders: number;
        pending_orders: number;
        cancelled_orders: number;
        // MS551-followup-A: tracked as integer cents internally; divided to
        // dollars when emitted via AdminVendorRow to keep the API boundary
        // unchanged.
        paid_revenue_cents: number;
        pending_revenue_cents: number;
        fulfilSamples: number[]; // hours
        last_order_at: string | null;
      };
      const buckets = new Map<string, Bucket>();

      // Seed from vendor_partners so even zero-order vendors appear.
      for (const p of partners) {
        buckets.set(keyOf(p.vendor_name), {
          partner: p,
          displayName: p.vendor_name,
          paid_orders: 0,
          pending_orders: 0,
          cancelled_orders: 0,
          paid_revenue_cents: 0,
          pending_revenue_cents: 0,
          fulfilSamples: [],
          last_order_at: null,
        });
      }

      // Roll orders into vendor buckets. Vendor name is the second half
      // of "<category> · <vendor_name>". If the parsed vendor doesn't
      // match a vendor_partners row, we still surface it under
      // is_known_partner=false so the operator notices the catalogue gap.
      for (const o of orders) {
        if (!o.cake_size || !o.cake_size.includes("·")) continue;
        const parts = o.cake_size.split("·").map((s) => s.trim());
        const vendorName = parts.slice(1).join(" · ").trim();
        if (!vendorName) continue;
        const key = keyOf(vendorName);
        let b = buckets.get(key);
        if (!b) {
          b = {
            partner: null,
            displayName: vendorName,
            paid_orders: 0,
            pending_orders: 0,
            cancelled_orders: 0,
            paid_revenue_cents: 0,
            pending_revenue_cents: 0,
            fulfilSamples: [],
            last_order_at: null,
          };
          buckets.set(key, b);
        }
        const amountCents = o.budget_cents ?? 0;
        if (o.status === "cancelled") {
          b.cancelled_orders += 1;
        } else if (o.payment_status === "paid") {
          b.paid_orders += 1;
          b.paid_revenue_cents += amountCents;
        } else {
          b.pending_orders += 1;
          b.pending_revenue_cents += amountCents;
        }
        if (o.status === "delivered" && o.created_at && o.updated_at) {
          try {
            const created = new Date(o.created_at).getTime();
            const updated = new Date(o.updated_at).getTime();
            const hrs = (updated - created) / (1000 * 60 * 60);
            // Sanity bound: ignore <0 (clock skew) or >90d (probably
            // status was changed manually long after creation).
            if (hrs > 0 && hrs < 90 * 24) b.fulfilSamples.push(hrs);
          } catch {
            // ignore
          }
        }
        if (o.created_at) {
          if (!b.last_order_at || o.created_at > b.last_order_at) {
            b.last_order_at = o.created_at;
          }
        }
      }

      const rows: AdminVendorRow[] = [];
      for (const [, b] of buckets) {
        const avg = b.fulfilSamples.length > 0
          ? b.fulfilSamples.reduce((a, x) => a + x, 0) / b.fulfilSamples.length
          : null;
        // MS306: derive paused flag. A known partner with zero
        // available SKUs is paused. Unknown partners (no vendor_partners
        // row, only an order trail) can't be paused via the catalogue
        // since they have no catalogue rows — treat as not paused.
        const availableSkuCount = b.partner
          ? skuCountByVendor.get(b.partner.id) ?? 0
          : 0;
        const isPaused = b.partner !== null && availableSkuCount === 0;
        rows.push({
          id: b.partner?.id ?? `unknown-${vendorSlug(b.displayName)}`,
          name: b.displayName,
          slug: vendorSlug(b.displayName),
          is_active: b.partner?.is_active ?? true, // unknowns assume active for visibility
          is_known_partner: Boolean(b.partner),
          contact_email: b.partner?.contact_email ?? null,
          paid_orders: b.paid_orders,
          pending_orders: b.pending_orders,
          cancelled_orders: b.cancelled_orders,
          // MS551-followup-A: divide cents → dollars once at the boundary.
          paid_revenue: b.paid_revenue_cents / 100,
          pending_revenue: b.pending_revenue_cents / 100,
          avg_fulfil_hours: avg,
          last_order_at: b.last_order_at,
          available_sku_count: availableSkuCount,
          is_paused: isPaused,
        });
      }

      // Default sort: paid revenue DESC. The page itself supports
      // re-sorting client-side so this is just the initial order.
      rows.sort((a, b) => b.paid_revenue - a.paid_revenue);
      return rows;
    },
  });
}

// Sprint 2.1d, Per-vendor drill-down. Hook for `/admin/vendors/:slug`.
// Returns the rolled-up summary (same shape as a single AdminVendorRow)
// + vendor profile fields + recent orders attributed to this vendor.
//
// We resolve the vendor by SLUG rather than ID so the URL is human-
// readable. Slugs are deterministic (vendor name → kebab-case), a
// vendor name change would invalidate bookmarks but is rare enough.
//
// "Unknown" vendors (appearing in cake_size but no vendor_partners
// row) get partial data, orders only, no profile. The page handles
// this gracefully so the operator can still see their performance.

export interface AdminVendorOrder {
  id: string;
  reference: string;
  team_member_name: string | null;
  product_name: string | null;       // cake_type
  budget_cents: number;
  status: string;
  payment_status: string | null;
  delivery_date: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AdminVendorDetail {
  summary: AdminVendorRow;            // rolled-up stats (matches list-page row)
  // Vendor profile (null when this vendor isn't in vendor_partners, i.e.
  // appearing in orders.cake_size but no canonical row).
  profile: {
    id: string | null;
    abn: string | null;
    contact_email: string | null;
    website: string | null;
    instagram: string | null;
    product_types: string[] | null;
    dietary_options: string[] | null;
    delivery_suburbs: string | null;
    is_active: boolean;
    created_at: string | null;
  };
  // Recent orders (most recent 50). Operator drills here to investigate
  // specific deliveries or to see what this vendor's been moving lately.
  orders: AdminVendorOrder[];
}

export function useAdminVendor(slug: string | undefined) {
  return useQuery({
    queryKey: ["admin", "vendors", slug],
    enabled: Boolean(slug),
    staleTime: 30_000,
    queryFn: async (): Promise<AdminVendorDetail | null> => {
      if (!slug) return null;
      // Two queries: vendor_partners (full profile by slug-matching name)
      // and orders (chat/browse-shape filtered to this vendor's name).
      // We resolve the canonical vendor name from vendor_partners first
      // because slug-collisions across casing variants would otherwise
      // pull the wrong rows.
      const [partnersRes, ordersRes, productsRes] = await Promise.all([
        supabase
          .from("vendor_partners")
          .select("id, vendor_name, abn, contact_email, website, instagram, product_types, dietary_options, delivery_suburbs, is_active, created_at"),
        supabase
          .from("orders")
          .select("id, reference, team_member_name, cake_type, cake_size, budget_cents, status, payment_status, delivery_date, created_at, updated_at")
          .like("cake_size", "%·%")
          .order("created_at", { ascending: false })
          // MS408: cap the DB scan — JS already slices to 50 rows for the
          // detail view. 200 gives a comfortable buffer without loading the
          // entire orders table on every /admin/vendors/[slug] open.
          .limit(200),
        // MS306: count available SKUs so the drill-down's summary
        // pill matches the list-page's pill (the AdminVendorRow
        // shape used in both surfaces). Inexpensive — same query
        // shape as useAdminVendors.
        supabase
          .from("vendor_products")
          .select("vendor_partner_id")
          .is("deleted_at", null)
          .eq("is_available", true),
      ]);
      if (partnersRes.error) throw partnersRes.error;
      if (ordersRes.error)   throw ordersRes.error;
      if (productsRes.error) throw productsRes.error;

      // MS306: SKU count map for the paused-state badge.
      const skuCountByVendor = new Map<string, number>();
      for (const p of (productsRes.data as { vendor_partner_id: string }[] | null) ?? []) {
        skuCountByVendor.set(
          p.vendor_partner_id,
          (skuCountByVendor.get(p.vendor_partner_id) ?? 0) + 1,
        );
      }

      type Partner = {
        id: string;
        vendor_name: string;
        abn: string | null;
        contact_email: string | null;
        website: string | null;
        instagram: string | null;
        product_types: string[] | null;
        dietary_options: string[] | null;
        delivery_suburbs: string | null;
        is_active: boolean | null;
        created_at: string | null;
      };
      type Order = {
        id: string;
        reference: string;
        team_member_name: string | null;
        cake_type: string | null;
        cake_size: string | null;
        budget_cents: number | null;
        status: string;
        payment_status: string | null;
        delivery_date: string | null;
        created_at: string | null;
        updated_at: string | null;
      };

      const partners = (partnersRes.data as Partner[] | null) ?? [];
      const orders   = (ordersRes.data as Order[] | null) ?? [];

      // Find the canonical partner by slug.
      const matchedPartner = partners.find((p) => vendorSlug(p.vendor_name) === slug) ?? null;
      const canonicalName = matchedPartner?.vendor_name ?? null;

      // Filter orders to this vendor. Match either by canonical name (when
      // we found the partner) or by slug-matching the parsed cake_size
      // vendor portion (for unknown vendors).
      const matchedOrders: AdminVendorOrder[] = [];
      let displayName = canonicalName;
      let firstSeenName: string | null = null;
      const fulfilSamples: number[] = [];
      let paidOrders = 0, pendingOrders = 0, cancelledOrders = 0;
      // MS551-followup-A: accumulate in integer cents; divide once at the boundary.
      let paidRevenueCents = 0, pendingRevenueCents = 0;
      let lastOrderAt: string | null = null;

      for (const o of orders) {
        if (!o.cake_size || !o.cake_size.includes("·")) continue;
        const parts = o.cake_size.split("·").map((s) => s.trim());
        const vendorName = parts.slice(1).join(" · ").trim();
        if (!vendorName) continue;
        const matches = canonicalName
          ? vendorName.trim().toLowerCase() === canonicalName.trim().toLowerCase()
          : vendorSlug(vendorName) === slug;
        if (!matches) continue;
        if (!firstSeenName) firstSeenName = vendorName;
        const amountCents = o.budget_cents ?? 0;
        if (o.status === "cancelled") {
          cancelledOrders += 1;
        } else if (o.payment_status === "paid") {
          paidOrders += 1;
          paidRevenueCents += amountCents;
        } else {
          pendingOrders += 1;
          pendingRevenueCents += amountCents;
        }
        if (o.status === "delivered" && o.created_at && o.updated_at) {
          try {
            const created = new Date(o.created_at).getTime();
            const updated = new Date(o.updated_at).getTime();
            const hrs = (updated - created) / (1000 * 60 * 60);
            if (hrs > 0 && hrs < 90 * 24) fulfilSamples.push(hrs);
          } catch {
            // ignore
          }
        }
        if (o.created_at && (!lastOrderAt || o.created_at > lastOrderAt)) {
          lastOrderAt = o.created_at;
        }
        matchedOrders.push({
          id: o.id,
          reference: o.reference,
          team_member_name: o.team_member_name,
          product_name: o.cake_type,
          budget_cents: amountCents,
          status: o.status,
          payment_status: o.payment_status,
          delivery_date: o.delivery_date,
          created_at: o.created_at,
          updated_at: o.updated_at,
        });
      }

      // Slug didn't match any partner AND no orders carry it either →
      // genuinely unknown vendor. Return null so the page can render a
      // 404-style state.
      if (!matchedPartner && matchedOrders.length === 0) {
        return null;
      }

      if (!displayName) displayName = firstSeenName ?? slug;

      const avgFulfil = fulfilSamples.length > 0
        ? fulfilSamples.reduce((a, b) => a + b, 0) / fulfilSamples.length
        : null;

      // MS306: derive paused flag from the SKU map. Drill-down matches
      // the index page so a vendor that shows "Paused · 0 active SKUs"
      // on /admin/vendors continues to read the same way on
      // /admin/vendors/<slug>.
      const availableSkuCount = matchedPartner
        ? skuCountByVendor.get(matchedPartner.id) ?? 0
        : 0;
      const isPaused = matchedPartner !== null && availableSkuCount === 0;

      const summary: AdminVendorRow = {
        id: matchedPartner?.id ?? `unknown-${slug}`,
        name: displayName,
        slug,
        is_active: matchedPartner?.is_active ?? true,
        is_known_partner: Boolean(matchedPartner),
        contact_email: matchedPartner?.contact_email ?? null,
        paid_orders: paidOrders,
        pending_orders: pendingOrders,
        cancelled_orders: cancelledOrders,
        paid_revenue: paidRevenueCents / 100,
        pending_revenue: pendingRevenueCents / 100,
        avg_fulfil_hours: avgFulfil,
        last_order_at: lastOrderAt,
        available_sku_count: availableSkuCount,
        is_paused: isPaused,
      };

      return {
        summary,
        profile: {
          id: matchedPartner?.id ?? null,
          abn: matchedPartner?.abn ?? null,
          contact_email: matchedPartner?.contact_email ?? null,
          website: matchedPartner?.website ?? null,
          instagram: matchedPartner?.instagram ?? null,
          product_types: matchedPartner?.product_types ?? null,
          dietary_options: matchedPartner?.dietary_options ?? null,
          delivery_suburbs: matchedPartner?.delivery_suburbs ?? null,
          is_active: matchedPartner?.is_active ?? true,
          created_at: matchedPartner?.created_at ?? null,
        },
        // Cap at 50 most-recent orders so the table stays light.
        orders: matchedOrders.slice(0, 50),
      };
    },
  });
}

/**
 * All bakery partners for /admin/partners. Mutations (activate/suspend,
 * profile edits) should invalidate ["admin", "partners"] to refresh.
 */
export function useAdminPartners() {
  return useQuery({
    queryKey: ["admin", "partners"],
    staleTime: 30_000,
    queryFn: async (): Promise<AdminPartnerRow[]> => {
      const { data, error } = await supabase
        .from("vendor_partners")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as unknown as AdminPartnerRow[] | null) ?? [];
    },
  });
}

/**
 * All companies + derived order and member counts for the admin companies
 * page. Bundles three queries (companies, orders-for-aggregation,
 * team_members-for-aggregation) and does the per-company rollups in the
 * queryFn. Reasonable for admin scale; if counts grow, move aggregation
 * server-side via views.
 */
export function useAdminCompanies() {
  return useQuery({
    queryKey: ["admin", "companies"],
    staleTime: 30_000,
    queryFn: async (): Promise<AdminCompanyRow[]> => {
      const [companiesRes, ordersRes, membersRes] = await Promise.all([
        // MS397: cap at 500; ordersRes/membersRes are intentionally unbounded
        // because they're aggregate-only (count per company) and must include all rows.
        supabase.from("companies").select("*").order("created_at", { ascending: false }).limit(500),
        supabase.from("orders").select("company_id, created_at"),
        supabase.from("team_members").select("company_id"),
      ]);
      if (companiesRes.error) throw companiesRes.error;

      const ordersByCompany: Record<string, { count: number; lastDate: string | null }> = {};
      for (const o of (ordersRes.data as { company_id: string; created_at: string | null }[] | null) ?? []) {
        const key = o.company_id;
        if (!ordersByCompany[key]) ordersByCompany[key] = { count: 0, lastDate: null };
        ordersByCompany[key].count++;
        if (
          o.created_at &&
          (!ordersByCompany[key].lastDate || o.created_at > ordersByCompany[key].lastDate!)
        ) {
          ordersByCompany[key].lastDate = o.created_at;
        }
      }

      const membersByCompany: Record<string, number> = {};
      for (const m of (membersRes.data as { company_id: string }[] | null) ?? []) {
        membersByCompany[m.company_id] = (membersByCompany[m.company_id] || 0) + 1;
      }

      const raw = (companiesRes.data as unknown as AdminCompanyRow[] | null) ?? [];
      return raw.map((c) => ({
        ...c,
        order_count: ordersByCompany[c.id]?.count || 0,
        last_order_date: ordersByCompany[c.id]?.lastDate || null,
        member_count: membersByCompany[c.id] || 0,
      }));
    },
  });
}

/**
 * Platform-wide orders for /admin/orders. Joins each order against its
 * company row client-side (small query, a few hundred companies tops)
 * so the list can show company names without a join on the server.
 * Callers mutating an order should invalidate
 * ["admin", "orders-with-companies"] to refresh this cache.
 */
export function useAdminOrdersWithCompanies() {
  return useQuery({
    queryKey: ["admin", "orders-with-companies"],
    staleTime: 15_000,
    // MS344: admin ops sit on /admin/orders to triage new orders as
    // they land. Pre-MS344 they needed a manual refresh (or tab-away/
    // back) to spot a new pending row. 60s matches the vendor portal
    // BakeryOrders cadence (MS343) — fast enough that a fresh order
    // surfaces before the admin's next sip of coffee, slow enough to
    // not hammer the orders join even as the table grows.
    refetchInterval: 60_000,
    queryFn: async (): Promise<AdminOrderRow[]> => {
      const [ordersRes, companiesRes] = await Promise.all([
        // MS397: cap at 500; admin can export/paginate if they need older rows.
        supabase.from("orders").select("*").order("created_at", { ascending: false }).limit(500),
        supabase.from("companies").select("id, name").limit(1000), // MS404: match MS402 cap on same polled query
      ]);
      if (ordersRes.error) throw ordersRes.error;
      if (companiesRes.error) throw companiesRes.error;

      const companyMap: Record<string, string> = {};
      (companiesRes.data || []).forEach((c) => {
        companyMap[c.id] = c.name;
      });

      // Generated types include more columns than AdminOrderRow cares about,
      // and company_name is only attached here (join done client-side), so
      // cast through unknown to appease strict-mode.
      const raw = (ordersRes.data as unknown as AdminOrderRow[] | null) ?? [];
      return raw.map((o) => ({
        ...o,
        company_name: companyMap[o.company_id] || "Unknown",
      }));
    },
  });
}

// Sprint 1.7l, funnel attribution shape consumed by /admin/funnel.
// Read from interaction_captures, aggregated client-side (volume is
// small enough, Lembra is at ~80 visitors/month, that pulling rows
// is cheaper than building a Postgres view).
//
// Five funnel stages (broadest → narrowest commitment):
//   1. arrived  , first event_type per session_id (smart_input
//                  submitted, chat session_opened, browse page_opened)
//   2. engaged  , cards_rendered (chat) OR filter_changed (browse)
//   3. selected , card_selected (either surface)
//   4. confirmed, order_confirm.sheet_opened
//   5. submitted, order_confirm.gift_request_submitted
//
// Plus three secondary cuts:
//   - surface_split: arrivals by entry surface
//   - bridge_clicks: how often visitors cross between surfaces
//   - smart_intent_distribution: how SmartIntentBar classifies inputs
export interface AdminFunnelStage {
  key: "arrived" | "engaged" | "selected" | "confirmed" | "submitted";
  label: string;
  count: number;
  conversionFromPrior: number | null; // 0..1, null for the top
}

export interface AdminFunnelSurfaceSplit {
  surface: string;
  count: number;
  share: number; // 0..1
}

export interface AdminFunnelBridge {
  from: string;
  to: string;
  count: number;
}

export interface AdminFunnel {
  totalEvents: number;
  windowDays: number;
  stages: AdminFunnelStage[];
  surfaceSplit: AdminFunnelSurfaceSplit[];
  bridgeClicks: AdminFunnelBridge[];
  // Daily new-arrival counts for the trend sparkline.
  dailyArrivals: Array<{ date: string; count: number }>;
  // Sprint 1.7q, revenue attribution. Reads orders in the window,
  // splits by source (chat vs browse vs legacy) using:
  //   chat_session_id IS NOT NULL → chat
  //   cake_size LIKE '%·%' AND chat_session_id IS NULL → browse
  //   else → legacy (pre-Sprint-1.7 cake flow)
  // Two values per source: paidRevenue (only payment_status='paid')
  // and pendingRevenue (created but unpaid, represents the
  // recovery-cron's address space).
  revenue: {
    paidTotal: number;
    pendingTotal: number;
    bySource: Array<{
      source: "chat" | "browse" | "legacy";
      paidRevenue: number;
      pendingRevenue: number;
      paidCount: number;
      pendingCount: number;
    }>;
  };
  // Sprint 2.0a, SmartIntentBar classifier verdict distribution.
  // Reads `interaction_captures` rows where surface='smart_input' +
  // event_type='classified' (success) or 'classify_failed' (the
  // classifier itself errored). Helps tune the classifier prompt.
  smartIntentClassification: {
    total: number;
    // 5 buckets in display order: chat / browse / nav / ambiguous / failed.
    // Counts include classify_failed under the "failed" bucket.
    breakdown: Array<{
      intent: "chat" | "browse" | "nav" | "ambiguous" | "failed";
      count: number;
      pct: number; // 0..1
    }>;
  };
}

/**
 * Sprint 1.7j, gift requests for /admin/gift-requests. Filters
 * orders to the Sprint 1.7+ shape (cake_size column carries the
 * "<vendor_category> · <vendor_name>" composite that submit-gift-
 * request writes, legacy cake orders never use the · separator).
 * Joins with celebration_recommendations + companies for the
 * triage view's per-row context.
 *
 * Mutations should invalidate ["admin", "gift-requests"].
 */
export function useAdminGiftRequests() {
  return useQuery({
    queryKey: ["admin", "gift-requests"],
    staleTime: 15_000,
    // MS344: AdminGiftRequests is the chat/browse-originated order
    // triage queue — admin needs the same auto-refresh cadence as
    // useAdminOrdersWithCompanies so a new chat-originated order
    // surfaces without a manual refresh.
    refetchInterval: 60_000,
    queryFn: async (): Promise<AdminGiftRequestRow[]> => {
      // Pull all orders + map company names + the linked recommendation.
      // The · separator filter narrows to Sprint 1.7+ rows; PostgREST's
      // `like` operator is case-sensitive which is what we want.
      const [ordersRes, companiesRes, recsRes] = await Promise.all([
        supabase
          .from("orders")
          .select("*")
          .like("cake_size", "%·%")
          .order("created_at", { ascending: false })
          .limit(500), // MS402: safety ceiling on polled gift-request queue
        supabase.from("companies").select("id, name").limit(1000),
        supabase
          .from("celebration_recommendations")
          .select("id, order_id, guest_session_id, status")
          .eq("generated_by", "customer_confirmation")
          .limit(500), // MS402: bound the rec join table
      ]);
      if (ordersRes.error) throw ordersRes.error;
      if (companiesRes.error) throw companiesRes.error;
      if (recsRes.error) throw recsRes.error;

      const companyMap: Record<string, string> = {};
      (companiesRes.data || []).forEach((c) => {
        companyMap[c.id] = c.name;
      });
      const recsByOrder: Record<string, { ref: string | null; status: string | null }> = {};
      (recsRes.data || []).forEach((r) => {
        if (r.order_id) {
          recsByOrder[r.order_id] = {
            ref: (r.guest_session_id as string | null) ?? null, // we stash the LEM-REQ-* into guest_session_id
            status: (r.status as string | null) ?? null,
          };
        }
      });

      type OrderShape = {
        id: string;
        reference: string;
        team_member_name: string;
        celebration_type: string;
        cake_type: string | null;
        cake_size: string | null;
        customer_email: string | null;
        customer_name: string | null;
        delivery_address: string | null;
        delivery_date: string | null;
        budget_cents: number;
        status: string;
        payment_status: string | null;
        company_id: string | null;
        chat_session_id: string | null;
        created_at: string | null;
        updated_at: string | null;
      };
      const raw = (ordersRes.data as unknown as OrderShape[] | null) ?? [];

      return raw.map((o) => {
        // Parse cake_size = "<category> · <vendor_name>"
        let category: string | null = null;
        let vendorName: string | null = null;
        if (o.cake_size && o.cake_size.includes("·")) {
          const [c, ...rest] = o.cake_size.split("·").map((s) => s.trim());
          category = c || null;
          vendorName = rest.join(" · ").trim() || null;
        }
        const rec = recsByOrder[o.id] ?? { ref: null, status: null };
        return {
          id: o.id,
          reference: o.reference,
          team_member_name: o.team_member_name,
          celebration_type: o.celebration_type,
          product_name: o.cake_type,
          vendor_category: category,
          vendor_name: vendorName,
          customer_email: o.customer_email,
          customer_name: o.customer_name,
          delivery_address: o.delivery_address,
          delivery_date: o.delivery_date,
          budget_cents: o.budget_cents,
          status: o.status,
          payment_status: o.payment_status,
          company_id: o.company_id,
          company_name: o.company_id ? (companyMap[o.company_id] ?? null) : null,
          recommendation_request_ref: rec.ref,
          recommendation_status: rec.status,
          chat_session_id: o.chat_session_id,
          created_at: o.created_at,
          updated_at: o.updated_at,
        };
      });
    },
  });
}

/**
 * Sprint 1.7l, funnel attribution from interaction_captures.
 * Reads N days of events (default 7), aggregates client-side, returns
 * the AdminFunnel shape for /admin/funnel to render.
 *
 * Why client-side aggregation:
 *   At Lembra's scale (~80 visitors/month, low thousands of events
 *   per month) a single SELECT is cheaper than building views or RPCs.
 *   When volume grows past tens of thousands of events per query
 *   window we'll move this into a Postgres materialised view.
 */
export function useAdminFunnel(daysBack: number = 7) {
  return useQuery({
    queryKey: ["admin", "funnel", daysBack],
    staleTime: 60_000,
    queryFn: async (): Promise<AdminFunnel> => {
      const since = new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString();
      const [eventsRes, ordersRes] = await Promise.all([
        supabase
          .from("interaction_captures")
          .select("surface, event_type, payload, created_at")
          .gte("created_at", since)
          .in("surface", ["smart_input", "chat", "browse", "order_confirm"]),
        // Sprint 1.7q, orders in the same window for revenue attribution.
        supabase
          .from("orders")
          .select("budget_cents, payment_status, chat_session_id, cake_size, created_at")
          .gte("created_at", since),
      ]);
      if (eventsRes.error) throw eventsRes.error;
      if (ordersRes.error) throw ordersRes.error;
      const rows = (eventsRes.data ?? []) as Array<{
        surface: string;
        event_type: string;
        payload: Record<string, unknown> | null;
        created_at: string;
      }>;
      const orderRows = (ordersRes.data ?? []) as Array<{
        budget_cents: number | null;
        payment_status: string | null;
        chat_session_id: string | null;
        cake_size: string | null;
        created_at: string;
      }>;

      // ── Stage 1: arrivals ──
      // Count distinct entry events. SmartIntentBar's `submitted` is
      // the first thing a visitor does there; chat's `session_opened`
      // and browse's `page_opened` are the equivalents on those
      // surfaces. We dedupe by (surface, payload.session_id) when
      // available so multi-event visitors don't double-count.
      const arrivalEvents = new Set<string>();
      for (const r of rows) {
        const isArrival =
          (r.surface === "smart_input" && r.event_type === "submitted") ||
          (r.surface === "chat" && r.event_type === "session_opened") ||
          (r.surface === "browse" && r.event_type === "page_opened");
        if (!isArrival) continue;
        const sid =
          (r.payload?.session_id as string | undefined) ??
          (r.payload?.guest_session_id as string | undefined) ??
          `${r.surface}:${r.created_at}`;
        arrivalEvents.add(`${r.surface}:${sid}`);
      }
      const arrived = arrivalEvents.size;

      // ── Stage 2: engaged ──
      // Visitors who got past the arrival event into "doing things":
      // chat cards_rendered or browse filter_changed.
      const engagedKeys = new Set<string>();
      for (const r of rows) {
        const isEngaged =
          (r.surface === "chat" && r.event_type === "cards_rendered") ||
          (r.surface === "browse" && r.event_type === "filter_changed");
        if (!isEngaged) continue;
        const sid =
          (r.payload?.session_id as string | undefined) ??
          (r.payload?.guest_session_id as string | undefined) ??
          `${r.surface}:${r.created_at}`;
        engagedKeys.add(`${r.surface}:${sid}`);
      }
      const engaged = engagedKeys.size;

      // ── Stage 3: selected ──
      // card_selected fires from both chat + browse when the user
      // taps "Send →" on a card.
      const selectedKeys = new Set<string>();
      for (const r of rows) {
        if (r.event_type !== "card_selected") continue;
        const sid =
          (r.payload?.session_id as string | undefined) ??
          (r.payload?.sku_id as string | undefined) ??
          `${r.surface}:${r.created_at}`;
        selectedKeys.add(`${r.surface}:${sid}`);
      }
      const selected = selectedKeys.size;

      // ── Stage 4 & 5: confirmed + submitted ──
      // Both come from order_confirm. sheet_opened is "got to the
      // confirm screen"; gift_request_submitted is "successfully
      // sent the request and got a Stripe redirect URL".
      let confirmed = 0;
      let submitted = 0;
      for (const r of rows) {
        if (r.surface !== "order_confirm") continue;
        if (r.event_type === "sheet_opened") confirmed++;
        else if (r.event_type === "gift_request_submitted") submitted++;
      }

      const stages: AdminFunnelStage[] = [
        { key: "arrived",   label: "Arrived",            count: arrived,   conversionFromPrior: null },
        { key: "engaged",   label: "Engaged with surface", count: engaged,  conversionFromPrior: arrived > 0 ? engaged / arrived : null },
        { key: "selected",  label: "Picked a SKU",       count: selected,  conversionFromPrior: engaged > 0 ? selected / engaged : null },
        { key: "confirmed", label: "Opened confirm sheet", count: confirmed, conversionFromPrior: selected > 0 ? confirmed / selected : null },
        { key: "submitted", label: "Submitted to Stripe", count: submitted, conversionFromPrior: confirmed > 0 ? submitted / confirmed : null },
      ];

      // ── Surface split (which entry generated the most arrivals) ──
      const bySurface: Record<string, number> = {};
      for (const r of rows) {
        const isArrival =
          (r.surface === "smart_input" && r.event_type === "submitted") ||
          (r.surface === "chat" && r.event_type === "session_opened") ||
          (r.surface === "browse" && r.event_type === "page_opened");
        if (!isArrival) continue;
        bySurface[r.surface] = (bySurface[r.surface] ?? 0) + 1;
      }
      const totalArrivals = Object.values(bySurface).reduce((s, n) => s + n, 0);
      const surfaceSplit: AdminFunnelSurfaceSplit[] = Object.entries(bySurface)
        .map(([surface, count]) => ({
          surface,
          count,
          share: totalArrivals > 0 ? count / totalArrivals : 0,
        }))
        .sort((a, b) => b.count - a.count);

      // ── Bridge clicks (cross-surface navigations) ──
      // bridge_to_chat_clicked is fired by browse when the user taps
      // either the empty-state or always-visible bridge link. The
      // payload.reason is "empty_results" or "secondary_cta".
      const bridges: Record<string, number> = {};
      for (const r of rows) {
        if (r.surface !== "browse" || r.event_type !== "bridge_to_chat_clicked") continue;
        const reason = String(r.payload?.reason ?? "unknown");
        const key = `browse_${reason} → chat`;
        bridges[key] = (bridges[key] ?? 0) + 1;
      }
      const bridgeClicks: AdminFunnelBridge[] = Object.entries(bridges)
        .map(([k, count]) => {
          const [from, to] = k.split(" → ");
          return { from, to, count };
        })
        .sort((a, b) => b.count - a.count);

      // ── Daily arrivals trend ──
      // Bucket arrivals by YYYY-MM-DD so the trend sparkline can
      // render even with ~10 events.
      const dailyMap: Record<string, number> = {};
      for (const r of rows) {
        const isArrival =
          (r.surface === "smart_input" && r.event_type === "submitted") ||
          (r.surface === "chat" && r.event_type === "session_opened") ||
          (r.surface === "browse" && r.event_type === "page_opened");
        if (!isArrival) continue;
        const day = r.created_at.slice(0, 10);
        dailyMap[day] = (dailyMap[day] ?? 0) + 1;
      }
      // Backfill empty days so the chart x-axis is continuous.
      const dailyArrivals: Array<{ date: string; count: number }> = [];
      for (let i = daysBack - 1; i >= 0; i--) {
        const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        dailyArrivals.push({ date: d, count: dailyMap[d] ?? 0 });
      }

      // ── Revenue attribution (Sprint 1.7q) ──
      // Source per order:
      //   chat_session_id IS NOT NULL → "chat"
      //   chat_session_id IS NULL AND cake_size has · separator → "browse"
      //   else → "legacy" (pre-Sprint-1.7 cake-flow orders)
      // MS551-followup-A: accumulate in integer cents internally.
      // The AdminFunnel revenue numbers are emitted as dollars (existing
      // consumer contract); divide once when constructing the response.
      const sourceCentsTotals: Record<"chat" | "browse" | "legacy", { paidRevenueCents: number; pendingRevenueCents: number; paidCount: number; pendingCount: number }> = {
        chat:   { paidRevenueCents: 0, pendingRevenueCents: 0, paidCount: 0, pendingCount: 0 },
        browse: { paidRevenueCents: 0, pendingRevenueCents: 0, paidCount: 0, pendingCount: 0 },
        legacy: { paidRevenueCents: 0, pendingRevenueCents: 0, paidCount: 0, pendingCount: 0 },
      };
      let paidTotalCents = 0;
      let pendingTotalCents = 0;
      for (const o of orderRows) {
        const source: "chat" | "browse" | "legacy" =
          o.chat_session_id ? "chat"
          : (o.cake_size && o.cake_size.includes("·")) ? "browse"
          : "legacy";
        const amountCents = o.budget_cents ?? 0;
        if (o.payment_status === "paid") {
          sourceCentsTotals[source].paidRevenueCents += amountCents;
          sourceCentsTotals[source].paidCount += 1;
          paidTotalCents += amountCents;
        } else if (o.payment_status === "unpaid") {
          sourceCentsTotals[source].pendingRevenueCents += amountCents;
          sourceCentsTotals[source].pendingCount += 1;
          pendingTotalCents += amountCents;
        }
      }
      const paidTotal = paidTotalCents / 100;
      const pendingTotal = pendingTotalCents / 100;

      // Sprint 2.0a, SmartIntentBar classification distribution.
      // Count `classified` events bucketed by payload.intent + count
      // `classify_failed` as a separate "failed" bucket. Unknown
      // intent strings (defensive, shouldn't happen) coerce to
      // "ambiguous" so they're visible somewhere.
      const classCounts: Record<"chat" | "browse" | "nav" | "ambiguous" | "failed", number> = {
        chat: 0, browse: 0, nav: 0, ambiguous: 0, failed: 0,
      };
      for (const r of rows) {
        if (r.surface !== "smart_input") continue;
        if (r.event_type === "classified") {
          const intent = String(r.payload?.intent ?? "").toLowerCase();
          if (intent === "chat" || intent === "browse" || intent === "nav" || intent === "ambiguous") {
            classCounts[intent] += 1;
          } else {
            classCounts.ambiguous += 1;
          }
        } else if (r.event_type === "classify_failed") {
          classCounts.failed += 1;
        }
      }
      const classTotal = Object.values(classCounts).reduce((a, b) => a + b, 0);
      const smartIntentClassification = {
        total: classTotal,
        breakdown: (["chat", "browse", "nav", "ambiguous", "failed"] as const).map((intent) => ({
          intent,
          count: classCounts[intent],
          pct: classTotal > 0 ? classCounts[intent] / classTotal : 0,
        })),
      };

      return {
        totalEvents: rows.length,
        windowDays: daysBack,
        stages,
        surfaceSplit,
        bridgeClicks,
        dailyArrivals,
        revenue: {
          paidTotal,
          pendingTotal,
          bySource: (["chat", "browse", "legacy"] as const).map((s) => ({
            source: s,
            // MS551-followup-A: divide cents → dollars once at the boundary
            // so AdminFunnel's revenue fields keep their existing semantics.
            paidRevenue: sourceCentsTotals[s].paidRevenueCents / 100,
            pendingRevenue: sourceCentsTotals[s].pendingRevenueCents / 100,
            paidCount: sourceCentsTotals[s].paidCount,
            pendingCount: sourceCentsTotals[s].pendingCount,
          })),
        },
        smartIntentClassification,
      };
    },
  });
}

/**
 * Platform-wide overview for /admin dashboards. Bundles four DB counts +
 * the orders full-scan used for revenue / recent-orders derivations into
 * one cache entry so the Admin dashboard doesn't re-fire them per visit.
 *
 * Orders are fetched in full here (not just recent) because the "this
 * month" derivation needs every order's delivery_date + status +
 * budget_cents. The count is small enough (a few thousand rows max) that this
 * is fine; if it ever grows we'll paginate and move the aggregation
 * server-side.
 */
export function useAdminOverview() {
  return useQuery({
    queryKey: ["admin", "platform-overview"],
    // Admin stats move more than customer stats, make them fresh.
    staleTime: 15_000,
    queryFn: async (): Promise<AdminOverview> => {
      const [
        companiesRes,
        ordersRes,
        partnersRes,
        pendingRes,
        activePartnerIdsRes,
        availableSkuVendorIdsRes,
      ] = await Promise.all([
        supabase.from("companies").select("*", { count: "exact", head: true }),
        supabase
          .from("orders")
          .select("budget_cents, delivery_date, status, reference, team_member_name, celebration_type, created_at, vendor_category")
          // MS408: cap unbounded scan — the admin overview aggregates
          // revenue + order counts. 2000 covers the operational window;
          // beyond this a Postgres view or materialized aggregate should
          // own the full-history rollup.
          .limit(2000),
        supabase
          .from("vendor_partners")
          .select("*", { count: "exact", head: true })
          .eq("is_active", true),
        supabase
          .from("bakery_applications")
          .select("*", { count: "exact", head: true })
          .eq("status", "pending"),
        // MS307: pull active partner IDs + their available-SKU vendor IDs
        // so we can derive pausedPartners = active partners with zero
        // is_available=true SKUs. Two cheap queries instead of a
        // server-side aggregate (no RPC for this exists today). Same
        // shape as the catalogue-honesty filters in MS283/296/304/306.
        supabase
          .from("vendor_partners")
          .select("id")
          .eq("is_active", true),
        supabase
          .from("vendor_products")
          .select("vendor_partner_id")
          .is("deleted_at", null)
          .eq("is_available", true),
      ]);

      const orders = (ordersRes.data as AdminRecentOrder[] | null) ?? [];
      const now = new Date();
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      const thisMonthOrders = orders.filter(
        (o) =>
          o.delivery_date &&
          isWithinInterval(parseISO(o.delivery_date), { start: monthStart, end: monthEnd }),
      );
      // MS551-followup-A: sum integer cents internally, divide once for the
      // dollar-denominated AdminOverview.revenueThisMonth field.
      const revenueThisMonthCents = thisMonthOrders
        .filter((o) => o.status === "delivered")
        .reduce((sum, o) => sum + (o.budget_cents ?? 0), 0);
      const revenueThisMonth = revenueThisMonthCents / 100;

      const recentOrders = [...orders]
        .sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bTime - aTime;
        })
        .slice(0, 5);

      // MS307: derive pausedPartners.
      const activePartnerIds = new Set(
        ((activePartnerIdsRes.data as { id: string }[] | null) ?? []).map((p) => p.id),
      );
      const vendorsWithAvailableSkus = new Set(
        ((availableSkuVendorIdsRes.data as { vendor_partner_id: string }[] | null) ?? [])
          .map((p) => p.vendor_partner_id),
      );
      let pausedPartners = 0;
      for (const id of activePartnerIds) {
        if (!vendorsWithAvailableSkus.has(id)) pausedPartners += 1;
      }

      return {
        totalCompanies: companiesRes.count ?? 0,
        totalOrders: orders.length,
        ordersThisMonth: thisMonthOrders.length,
        revenueThisMonth,
        activePartners: partnersRes.count ?? 0,
        pausedPartners,
        pendingApplications: pendingRes.count ?? 0,
        recentOrders,
      };
    },
  });
}

// ── Sprint 1.7t, Chat economics (LLM cost visibility) ─────────────
//
// Aggregates chat_sessions cost / token / status data + the orders ↔
// sessions link (via orders.chat_session_id, added in 1.7p) so /admin/
// funnel can render a "Chat economics" card. Answers:
//
//   - How much are we spending on LLM calls (last 7d / 30d)?
//   - What's the avg cost per chat session?
//   - What's the avg cost per CONVERTED order (sessions → paid orders)?
//   - How often is the per-session circuit breaker firing?
//   - 14-day daily cost trend so anomalies are visible at a glance
//
// The circuit-breaker count is the leading indicator we care about most:
// a few hits per week is normal (curious users probing the limits), but
// a spike would signal either a prompt regression or someone abusing
// the open chat endpoint. Catching that before it becomes a $200 bill
// is the operational point of this card.

export interface AdminChatEconomicsDailyPoint {
  day: string;        // YYYY-MM-DD
  cost_cents: number;
  sessions: number;
}

export interface AdminChatEconomics {
  totalCostCents30d: number;
  totalCostCents7d: number;
  sessionCount30d: number;
  sessionCount7d: number;
  avgCostPerSessionCents: number;     // 30d basis
  convertedSessionCount30d: number;   // sessions linked to a paid order
  avgCostPerConvertedOrderCents: number;
  circuitBreakerHits30d: number;
  daily: AdminChatEconomicsDailyPoint[]; // last 14 days, oldest → newest
  // Sprint 2.0b, chat-specific outcome funnel. Four stages on a 30-day
  // basis. Pairs with the cost / session metrics above to give a
  // complete chat-quality view: how many enter, how many get a
  // recommendation, how many submit, how many actually pay.
  outcomeFunnel: AdminChatOutcomeFunnel;
  // Sprint 2.2a, first-time vs returning visitor conversion split.
  // "Returning" = the user_id (authed) or guest_session_id (anon) had
  // an earlier chat_session within the 90-day lookback. Approximate
  // (a returning visitor whose first session was 91+ days ago shows as
  // first-time) but accurate enough to spot whether chat is winning
  // new customers or driving repeat business.
  cohortRetention: AdminChatCohortRetention;
  // Sprint 2.0c, distribution of "first chat message → paid order"
  // durations. Reveals whether chat is a same-session conversion (the
  // customer decides + pays in one sitting) or a multi-touch journey
  // (they leave, come back later, then convert). The headline
  // sameSessionShare collapses the distribution into one number Lucky
  // can scan; the buckets array drives the histogram.
  timeToConversion: AdminChatTimeToConversion;
  // Sprint 2.0e, top N SKUs the LLM has picked for present_recommendations
  // over the 30-day window. Frequency signal informs catalogue
  // bias: SKUs picked repeatedly are good candidates to feature on
  // /browse, on the marketing landing, or in seasonal hampers; SKUs
  // never picked are catalogue-rot candidates. Built from the same
  // chat_messages.rendered_sku_ids array the outcome funnel reads.
  topRecommendedSkus: AdminTopRecommendedSku[];
}

// Sprint 2.0e, per-SKU pick frequency in the 30-day window.
// Hydrated against vendor_products so the card can render the actual
// product photography + vendor name without a second client-side
// fetch. Sorted desc by pickCount in the returned array.
export interface AdminTopRecommendedSku {
  sku_id: string;
  name: string;
  vendor_name: string;
  vendor_category: string;
  image_url: string | null;
  price_aud: number;
  pickCount: number;
  // Share of this SKU vs. all picks in the window. Useful as a
  // secondary annotation, a high single-digit % is concentration
  // worth investigating ("the LLM keeps reaching for the same
  // option" can be either signal-of-quality or model-stuckness).
  pctOfPicks: number;
}

// Sprint 2.0c, single-bucket entry in the time-to-conversion
// histogram. Buckets are mutually exclusive + collectively exhaustive
// (every paid converted session in the window lands in exactly one).
export interface AdminChatTimeToConversionBucket {
  key: "lt_15m" | "lt_1h" | "lt_4h" | "lt_24h" | "gte_24h";
  label: string;            // human, e.g. "Under 15 min"
  count: number;
  pctOfTotal: number;       // 0..1
}

export interface AdminChatTimeToConversion {
  windowDays: number;
  totalConvertedSessions: number;
  // Same-session = converted within 1 hour of first message. The
  // industry shorthand for "they didn't have to think about it" —
  // most paid checkouts that happen this fast are within the original
  // browser tab without leaving. Higher = chat is closing in the
  // moment; lower = chat plants the seed but conversion happens via
  // a return touchpoint (success-page magic-link, dashboard re-entry,
  // /wishlist deep-link).
  sameSessionShare: number;             // 0..1
  // Median + p75 in minutes. Useful as text annotations beside the
  // histogram so the long tail doesn't pull the eye.
  medianMinutes: number | null;         // null when totalConvertedSessions === 0
  p75Minutes: number | null;
  buckets: AdminChatTimeToConversionBucket[];
}

export interface AdminChatCohortStat {
  sessions: number;          // total sessions in the 30d window for this cohort
  converted: number;         // sessions that produced any order
  paidConverted: number;     // sessions whose order is paid
  convRate: number;          // 0..1, paidConverted / sessions
}

export interface AdminChatCohortRetention {
  windowDays: number;        // analysis window (30)
  lookbackDays: number;      // classification lookback (90)
  firstTime: AdminChatCohortStat;
  returning: AdminChatCohortStat;
  // Returning-visitor share, the headline number. A high value (>30%)
  // means chat is generating real repeat business; near-zero means
  // every session is a fresh acquisition (Lucky's current state).
  returningShare: number;    // 0..1
}

export interface AdminChatOutcomeFunnelStage {
  key: "started" | "rendered" | "submitted" | "paid";
  label: string;
  count: number;
  pctOfStart: number;            // 0..1, this stage / started
  pctOfPrior: number | null;     // 0..1, this stage / previous (null for "started")
}

export interface AdminChatOutcomeFunnel {
  windowDays: number;
  stages: AdminChatOutcomeFunnelStage[];
}

export function useAdminChatEconomics() {
  return useQuery({
    queryKey: ["admin", "chat-economics"],
    staleTime: 60_000,
    queryFn: async (): Promise<AdminChatEconomics> => {
      const now = new Date();
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const fourteenDaysAgo = new Date(now);
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      // Sprint 2.2a, extended fetch window to 90 days so we can
      // classify each in-window session as first-time vs returning
      // (returning = the same user_id / guest_session_id had a chat
      // session in the prior 60 days). Cost/economics aggregation
      // still uses the 30-day window inside the loop.
      const ninetyDaysAgo = new Date(now);
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

      const [sessionsRes, ordersRes, renderedRes] = await Promise.all([
        // Pull only the columns we need. The cost / status / created_at
        // give us the aggregation surface; id is for the orders join.
        // 90-day window for cohort classification (Sprint 2.2a), the
        // 30-day economics aggregation reads off the same dataset.
        // MS573: is_active_draft added so admin tooling can later
        // surface "drafts-in-progress" alongside the existing
        // economics metrics (read-only, no display change in this PR).
        supabase
          .from("chat_sessions")
          .select("id, user_id, guest_session_id, total_cost_usd_cents, status, is_active_draft, created_at")
          .gte("created_at", ninetyDaysAgo.toISOString()),
        // Orders that came from chat (chat_session_id set), regardless of
        // payment_status, we count all CHAT-driven sessions as
        // "converted" (the customer at least submitted), and treat paid
        // separately for the cost-per-paid-order metric below.
        supabase
          .from("orders")
          .select("chat_session_id, payment_status, created_at")
          .gte("created_at", thirtyDaysAgo.toISOString())
          .not("chat_session_id", "is", null),
        // Sprint 2.0b, sessions where the LLM produced a recommendation.
        // Server-side signal (set when chat-concierge writes the
        // present_recommendations tool_use to chat_messages) so it
        // captures the model's behaviour even if the customer's tab
        // closed before client-side render fired.
        // Sprint 2.0e, also pull rendered_sku_ids so we can aggregate
        // per-SKU pick frequency for the top-recommended-SKUs card.
        // Same row count as before, just one extra column.
        supabase
          .from("chat_messages")
          .select("session_id, rendered_sku_ids")
          .not("rendered_sku_ids", "is", null)
          .gte("created_at", thirtyDaysAgo.toISOString()),
      ]);

      if (sessionsRes.error)  throw sessionsRes.error;
      if (ordersRes.error)    throw ordersRes.error;
      if (renderedRes.error)  throw renderedRes.error;

      type SessionRow = {
        id: string;
        user_id: string | null;
        guest_session_id: string | null;
        total_cost_usd_cents: number | null;
        status: string | null;
        // MS573: per-recipient draft flag — exposed so admin tooling
        // can later surface "drafts in progress." Read-only, no
        // aggregation in this PR (display surfacing is follow-up).
        is_active_draft: boolean | null;
        created_at: string | null;
      };
      type OrderRow = {
        chat_session_id: string | null;
        payment_status: string | null;
        created_at: string | null;
      };

      const sessions90d = (sessionsRes.data as unknown as SessionRow[] | null) ?? [];
      const orders      = (ordersRes.data as unknown as OrderRow[] | null) ?? [];
      const renderedRows = (renderedRes.data as unknown as { session_id: string | null; rendered_sku_ids: string[] | null }[] | null) ?? [];

      // Sprint 2.2a, slice to the 30-day analysis window for everything
      // EXCEPT cohort classification (which needs the 90-day lookback to
      // know whether a session is the visitor's first or a return).
      const sessions = sessions90d.filter((s) => {
        if (!s.created_at) return false;
        return new Date(s.created_at) >= thirtyDaysAgo;
      });

      // Build the converted-session set (sessions that produced ANY
      // order, paid or otherwise). Distinct on chat_session_id.
      const convertedSet = new Set<string>();
      for (const o of orders) {
        if (o.chat_session_id) convertedSet.add(o.chat_session_id);
      }

      // Build the paid-converted-session set (sessions whose order is
      // payment_status='paid'). Used for the cost-per-paid metric.
      const paidConvertedSet = new Set<string>();
      for (const o of orders) {
        if (o.chat_session_id && o.payment_status === "paid") {
          paidConvertedSet.add(o.chat_session_id);
        }
      }

      let totalCost30d = 0;
      let totalCost7d = 0;
      let sessions7d = 0;
      let circuitBreakerHits = 0;
      let paidConvertedCostSum = 0;

      // Daily bucket (last 14d). Init all 14 days to zero so the chart
      // doesn't have gaps when there's no traffic on a given day.
      const dailyMap: Record<string, AdminChatEconomicsDailyPoint> = {};
      for (let i = 0; i < 14; i++) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const day = d.toISOString().slice(0, 10);
        dailyMap[day] = { day, cost_cents: 0, sessions: 0 };
      }

      for (const s of sessions) {
        const cost = Number(s.total_cost_usd_cents ?? 0);
        totalCost30d += cost;
        if (s.status === "circuit_broken") circuitBreakerHits += 1;
        if (s.id && paidConvertedSet.has(s.id)) {
          paidConvertedCostSum += cost;
        }
        if (s.created_at) {
          const created = new Date(s.created_at);
          if (created >= sevenDaysAgo) {
            totalCost7d += cost;
            sessions7d += 1;
          }
          if (created >= fourteenDaysAgo) {
            const day = s.created_at.slice(0, 10);
            if (dailyMap[day]) {
              dailyMap[day].cost_cents += cost;
              dailyMap[day].sessions += 1;
            }
          }
        }
      }

      const avgCostPerSession = sessions.length > 0
        ? totalCost30d / sessions.length
        : 0;
      const avgCostPerConvertedOrder = paidConvertedSet.size > 0
        ? paidConvertedCostSum / paidConvertedSet.size
        : 0;

      // Sort oldest → newest so the line chart reads left-to-right.
      const daily = Object.values(dailyMap).sort((a, b) =>
        a.day.localeCompare(b.day),
      );

      // Sprint 2.0b, chat outcome funnel. Build the four stages on a
      // 30-day basis. Distinct-by-session_id at every stage so we count
      // sessions, not events. The "rendered" set comes from the
      // chat_messages query (rendered_sku_ids non-null) intersected with
      // sessions in our 30-day window, protects against multi-message
      // double-counting and against rendered events from older sessions
      // whose chat_session row is outside the window.
      const sessionIdSet = new Set(sessions.map((s) => s.id));
      const renderedSet = new Set<string>();
      for (const r of renderedRows) {
        if (r.session_id && sessionIdSet.has(r.session_id)) {
          renderedSet.add(r.session_id);
        }
      }
      const rawStages: Array<{
        key: AdminChatOutcomeFunnelStage["key"];
        label: string;
        count: number;
      }> = [
        { key: "started",   label: "Started",            count: sessions.length },
        { key: "rendered",  label: "Recommendations",    count: renderedSet.size },
        { key: "submitted", label: "Submitted",          count: convertedSet.size },
        { key: "paid",      label: "Paid",               count: paidConvertedSet.size },
      ];
      const startCount = rawStages[0].count;
      const stages: AdminChatOutcomeFunnelStage[] = rawStages.map((s, idx) => ({
        ...s,
        pctOfStart: startCount > 0 ? s.count / startCount : 0,
        pctOfPrior: idx === 0
          ? null
          : (rawStages[idx - 1].count > 0
              ? s.count / rawStages[idx - 1].count
              : 0),
      }));
      const outcomeFunnel: AdminChatOutcomeFunnel = {
        windowDays: 30,
        stages,
      };

      // Sprint 2.2a, first-time vs returning cohort classification.
      // For each in-window session, key by user_id (authed) or
      // guest_session_id (anon). If any earlier session in the 90-day
      // fetch shares that key, the session is "returning"; otherwise
      // "first-time". The 60-day buffer between window edge (30d) and
      // fetch edge (90d) means we'd only misclassify visitors who
      // returned after a 60+ day gap, acceptable for a coarse cohort
      // signal at Lucky's current scale.
      //
      // Sessions with neither user_id nor guest_session_id (shouldn't
      // happen given the exactly_one_owner CHECK constraint, but
      // defensive) are treated as first-time so we don't drop them.
      const firstTimeStat: AdminChatCohortStat = { sessions: 0, converted: 0, paidConverted: 0, convRate: 0 };
      const returningStat: AdminChatCohortStat = { sessions: 0, converted: 0, paidConverted: 0, convRate: 0 };
      const earliestByKey = new Map<string, number>(); // key → earliest created_at ms
      for (const s of sessions90d) {
        if (!s.created_at) continue;
        const key = s.user_id ?? s.guest_session_id;
        if (!key) continue;
        const ts = new Date(s.created_at).getTime();
        const prior = earliestByKey.get(key);
        if (prior === undefined || ts < prior) earliestByKey.set(key, ts);
      }
      for (const s of sessions) {
        const key = s.user_id ?? s.guest_session_id;
        if (!s.created_at) continue;
        const ts = new Date(s.created_at).getTime();
        const earliest = key ? earliestByKey.get(key) : undefined;
        // Returning if some earlier session for the same key exists.
        const isReturning = key !== null && earliest !== undefined && earliest < ts;
        const target = isReturning ? returningStat : firstTimeStat;
        target.sessions += 1;
        if (s.id && convertedSet.has(s.id)) target.converted += 1;
        if (s.id && paidConvertedSet.has(s.id)) target.paidConverted += 1;
      }
      firstTimeStat.convRate = firstTimeStat.sessions > 0
        ? firstTimeStat.paidConverted / firstTimeStat.sessions
        : 0;
      returningStat.convRate = returningStat.sessions > 0
        ? returningStat.paidConverted / returningStat.sessions
        : 0;
      const totalSessions = firstTimeStat.sessions + returningStat.sessions;
      const cohortRetention: AdminChatCohortRetention = {
        windowDays: 30,
        lookbackDays: 90,
        firstTime: firstTimeStat,
        returning: returningStat,
        returningShare: totalSessions > 0 ? returningStat.sessions / totalSessions : 0,
      };

      // Sprint 2.0c, time-to-conversion histogram. For each paid
      // converted session in the 30-day window, compute the duration
      // between the chat_session created_at (proxy for "first user
      // message", the session row is created by chat-concierge on
      // the first turn) and the linked paid order's created_at
      // (proxy for "ordered", Stripe Checkout completion typically
      // fires the webhook within ~30s, so order.created_at is close
      // enough to "paid" for bucketing). Excludes sessions linked to
      // multiple paid orders (rare; we take the earliest order's
      // duration in that case).
      const sessionCreatedAtById = new Map<string, number>();
      for (const s of sessions) {
        if (s.id && s.created_at) {
          sessionCreatedAtById.set(s.id, new Date(s.created_at).getTime());
        }
      }
      // Earliest paid order per session, handles the multi-paid-order
      // edge case + filters to paid only.
      const earliestPaidOrderByChatSession = new Map<string, number>();
      for (const o of orders) {
        if (!o.chat_session_id || o.payment_status !== "paid" || !o.created_at) continue;
        const ts = new Date(o.created_at).getTime();
        const prior = earliestPaidOrderByChatSession.get(o.chat_session_id);
        if (prior === undefined || ts < prior) {
          earliestPaidOrderByChatSession.set(o.chat_session_id, ts);
        }
      }
      // Build the per-session duration list (in minutes).
      const durationsMin: number[] = [];
      for (const [chatSessionId, paidTs] of earliestPaidOrderByChatSession) {
        const sessionTs = sessionCreatedAtById.get(chatSessionId);
        if (sessionTs === undefined) continue; // paid order's chat session is outside the 30d window
        const diffMin = (paidTs - sessionTs) / 60_000;
        if (diffMin < 0) continue; // defensive: clock skew or backfill
        durationsMin.push(diffMin);
      }
      // Bucket spec. Boundaries chosen to read as natural cliffs:
      //  - <15m → "decided in the moment"
      //  - <1h → "decided within the hour"
      //  - <4h → "decided within the day"
      //  - <24h → "overnight return"
      //  - ≥24h → "multi-day consideration"
      type BucketKey = AdminChatTimeToConversionBucket["key"];
      const bucketSpec: Array<{
        key: BucketKey;
        label: string;
        upperBoundMin: number;          // exclusive upper bound; Infinity for the last bucket
      }> = [
        { key: "lt_15m",  label: "Under 15 min",     upperBoundMin: 15 },
        { key: "lt_1h",   label: "15 min – 1 hour",  upperBoundMin: 60 },
        { key: "lt_4h",   label: "1 – 4 hours",      upperBoundMin: 240 },
        { key: "lt_24h",  label: "4 – 24 hours",     upperBoundMin: 1440 },
        { key: "gte_24h", label: "More than a day",  upperBoundMin: Infinity },
      ];
      const bucketCounts = new Map<BucketKey, number>(bucketSpec.map((b) => [b.key, 0]));
      for (const d of durationsMin) {
        for (const spec of bucketSpec) {
          if (d < spec.upperBoundMin) {
            bucketCounts.set(spec.key, (bucketCounts.get(spec.key) ?? 0) + 1);
            break;
          }
        }
      }
      const totalConverted = durationsMin.length;
      const buckets: AdminChatTimeToConversionBucket[] = bucketSpec.map((spec) => {
        const count = bucketCounts.get(spec.key) ?? 0;
        return {
          key: spec.key,
          label: spec.label,
          count,
          pctOfTotal: totalConverted > 0 ? count / totalConverted : 0,
        };
      });
      // Same-session = first two buckets combined (< 1 hour). Tuned
      // for chat which is a same-tab flow; 1h is the natural "they
      // didn't leave and come back" ceiling.
      const sameSessionCount = (bucketCounts.get("lt_15m") ?? 0) + (bucketCounts.get("lt_1h") ?? 0);
      const sameSessionShare = totalConverted > 0 ? sameSessionCount / totalConverted : 0;
      // Median + p75, sort once, index.
      const sorted = [...durationsMin].sort((a, b) => a - b);
      const pickPercentile = (p: number): number | null => {
        if (sorted.length === 0) return null;
        const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * p));
        return Math.round(sorted[idx]);
      };
      const timeToConversion: AdminChatTimeToConversion = {
        windowDays: 30,
        totalConvertedSessions: totalConverted,
        sameSessionShare,
        medianMinutes: pickPercentile(0.5),
        p75Minutes: pickPercentile(0.75),
        buckets,
      };

      // Sprint 2.0e, top-recommended SKU aggregation. Walks the
      // already-fetched rendered_sku_ids arrays (scoped to in-window
      // sessions via the same sessionIdSet the funnel uses, so we
      // don't count a recommendation from a session whose chat row
      // is outside the 30-day window). Counts every appearance of a
      // sku_id in any present_recommendations call, the model
      // showed it, regardless of whether the customer ordered it.
      // That's the right semantic for "what chat keeps surfacing".
      const skuPickCounts = new Map<string, number>();
      let totalPicks = 0;
      for (const r of renderedRows) {
        if (!r.session_id || !sessionIdSet.has(r.session_id)) continue;
        const ids = r.rendered_sku_ids;
        if (!Array.isArray(ids)) continue;
        for (const id of ids) {
          if (typeof id !== "string" || !id) continue;
          skuPickCounts.set(id, (skuPickCounts.get(id) ?? 0) + 1);
          totalPicks += 1;
        }
      }
      // Sort desc by pick count, take top 10. Hydrate against
      // vendor_products + vendor_partners for the card-renderable
      // shape. Single fetch keyed on the top sku_ids only, bounded
      // by 10 rows.
      const sortedSkuIds = Array.from(skuPickCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([id]) => id);
      let topRecommendedSkus: AdminTopRecommendedSku[] = [];
      if (sortedSkuIds.length > 0) {
        const { data: skuRows } = await supabase
          .from("vendor_products")
          .select("id, name, image_url, price, vendor_category, vendor_partners(vendor_name)")
          .in("id", sortedSkuIds);
        type SkuRow = {
          id: string;
          name: string | null;
          image_url: string | null;
          price: number | string | null;
          vendor_category: string | null;
          vendor_partners: { vendor_name?: string } | null;
        };
        const byId = new Map<string, SkuRow>();
        for (const r of (skuRows as SkuRow[] | null) ?? []) byId.set(r.id, r);
        // Walk sortedSkuIds (NOT skuRows) so the output preserves
        // the pick-count desc order. Skip rows where the SKU was
        // deleted between the recommendation and now (defensive —
        // catalogue churn can orphan past picks).
        topRecommendedSkus = sortedSkuIds.flatMap((id) => {
          const row = byId.get(id);
          if (!row) return [];
          const count = skuPickCounts.get(id) ?? 0;
          return [{
            sku_id: id,
            name: String(row.name ?? "Untitled"),
            vendor_name: row.vendor_partners?.vendor_name ?? "Lembra",
            vendor_category: String(row.vendor_category ?? "other"),
            image_url: row.image_url,
            price_aud: Number(row.price ?? 0),
            pickCount: count,
            pctOfPicks: totalPicks > 0 ? count / totalPicks : 0,
          }];
        });
      }

      return {
        totalCostCents30d: Math.round(totalCost30d),
        totalCostCents7d: Math.round(totalCost7d),
        sessionCount30d: sessions.length,
        sessionCount7d: sessions7d,
        avgCostPerSessionCents: Math.round(avgCostPerSession),
        convertedSessionCount30d: convertedSet.size,
        avgCostPerConvertedOrderCents: Math.round(avgCostPerConvertedOrder),
        circuitBreakerHits30d: circuitBreakerHits,
        daily,
        outcomeFunnel,
        cohortRetention,
        timeToConversion,
        topRecommendedSkus,
      };
    },
  });
}
